import React, { FC, useCallback, useContext, useMemo, useRef } from "react";
import { createContext } from "../../../util/globalContextSelector/ContextSelector";
import { FocusMode } from "../../focusMode";

type MenuParentType = "menubar" | "menu" | false;

export interface MenuParentContextShape {
    /**
     * When true, this allows click events to propagate out of the Menu. This helps fix errors when
     * the Menu is used within a `ClickAwayListener`. By default, click event propagation was
     * blocked to reduce event noise in parent components. This prop will likely change its default
     * to `true` in a future release.
     */
    allowClickPropagation: boolean;

    /**
     * Function that allows closing all submenus that have registered as "open" within
     * a given menu.
     */
    closeAllSubMenus: VoidFunction;

    /**
     * Proxy to the top-level Menu's onCloseRequested function to allow
     * anything inside the menu to close it.
     */
    closeMenuRootRequested: VoidFunction | null;

    /**
     * Either mouse or keyboard, we'll handle events differently in either case.
     */
    focusMode: FocusMode;

    /**
     * If true, the children of the menu have the opportunity to look at this
     * when rendering to see if they should include a gutter to fit the
     * contentBefore. This gutter is used for icons.
     */
    hasContentBefore: boolean;

    /**
     * The way menu items are displayed.
     * Compact stands for a 32px height menu item.
     * Normal stands for a 40px height menu item.
     */
    layout: "compact" | "normal";

    /**
     * Used to track where in the menubar/menu/submenu hierachy an element is rendered.
     */
    parent: MenuParentType;

    /**
     * Given a function to close the submenu, returns a function unregister this submenu.
     */
    registerSubMenu: (closeCallback: VoidFunction) => VoidFunction;
}

const noop = () => undefined;

const defaultContextValue = {
    closeAllSubMenus: noop,
    closeMenuRootRequested: null,
    focusMode: "mouse",
    hasContentBefore: false,
    parent: false,
    registerSubMenu: () => noop,
    layout: "compact",
    allowClickPropagation: false,
} as const;

const MenuParentContext = createContext<MenuParentContextShape>(defaultContextValue, "MenuParentContext");

export const useMenuParentContext = () => useContext(MenuParentContext);

type MenuParentContextProviderProps = Pick<
    MenuParentContextShape,
    "parent" | "hasContentBefore" | "focusMode" | "layout" | "allowClickPropagation"
> & {
    onCloseRequested: VoidFunction;
};

export const MenuParentContextProvider: FC<MenuParentContextProviderProps> = ({
    children,
    focusMode,
    hasContentBefore,
    onCloseRequested,
    parent,
    layout,
    allowClickPropagation,
}) => {
    // Array tracking all submenus in this Menu.
    const subMenuRegistrations = useRef<VoidFunction[]>([]);
    const closeAllSubMenus = useCallback(() => {
        subMenuRegistrations.current.forEach((cb) => cb());
    }, []);

    const registerSubMenu = useCallback<MenuParentContextShape["registerSubMenu"]>((cb) => {
        subMenuRegistrations.current.push(cb);
        return () => {
            subMenuRegistrations.current = subMenuRegistrations.current.filter((callback) => callback !== cb);
        };
    }, []);

    const parentContextValue = useMemo<MenuParentContextShape>(
        () => ({
            closeAllSubMenus,
            closeMenuRootRequested: onCloseRequested,
            focusMode,
            hasContentBefore,
            parent,
            registerSubMenu,
            layout,
            allowClickPropagation,
        }),
        [
            closeAllSubMenus,
            onCloseRequested,
            focusMode,
            hasContentBefore,
            layout,
            parent,
            registerSubMenu,
            allowClickPropagation,
        ]
    );

    return <MenuParentContext.Provider value={parentContextValue}>{children}</MenuParentContext.Provider>;
};
